import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Checkbox,
  Autocomplete,
  createFilterOptions,
  Tabs,
  Tab,
  Menu,
  Drawer,
  ListItemButton,
  ListItemIcon,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
  Assignment as BriefingIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Lightbulb as LightbulbIcon,
  AutoAwesome as MIAIcon,
  Check as CheckIcon,
  Inventory as ProductIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  AddCircle as AddMoreIcon,
  Link as LinkIcon,
  Block as BlockIcon,
  Tune as TuneIcon,
  LocalOffer as TagIcon,
  Person as PersonIcon,
  ChatBubbleOutline as MessageIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import StandardSnackbar from '../components/molecules/StandardSnackbar/StandardSnackbar';
import ModernToast from '../components/molecules/ModernToast/ModernToast';
import api from '../services/api';
import { useBrand } from '../context/BrandContext';
import PlatformDialog from '../components/molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../components/molecules/PlatformDialog/PlatformDialogTitle';
import BottomSheetHandle from '../components/atoms/BottomSheetHandle';

const filter = createFilterOptions();

/**
 * Página de Briefing - Gerenciamento de palavras-chave
 *
 * Permite ao usuário configurar:
 * - Keywords da sua marca (primárias)
 * - Keywords de concorrentes
 * - Configurações de análise automática
 */
const BriefingPage = () => {
  const { activeBrand } = useBrand();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Estados
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });



  // Estados para edição do briefing
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [editingBriefingName, setEditingBriefingName] = useState('');
  const [editingBriefingDescription, setEditingBriefingDescription] = useState('');

  // Estados para novo briefing
  const [isCreating, setIsCreating] = useState(false);
  const [newBriefingName, setNewBriefingName] = useState('');
  const [newBriefingDescription, setNewBriefingDescription] = useState('');
  const [suggestingDescription, setSuggestingDescription] = useState(false);

  // Estados para adicionar keywords
  const [newPrimaryKeyword, setNewPrimaryKeyword] = useState('');
  const [newCompetitorKeyword, setNewCompetitorKeyword] = useState('');
  const [newCompetitorName, setNewCompetitorName] = useState('');

  // Estados para sugestões de keywords
  const [suggestedKeywords, setSuggestedKeywords] = useState([]);
  const [selectedSuggestedKeywords, setSelectedSuggestedKeywords] = useState([]);
  const [keywordDialogOpen, setKeywordDialogOpen] = useState(false);
  const [keywordDialogType, setKeywordDialogType] = useState('primary'); // 'primary' ou 'competitor'
  const [keywordDialogCompetitor, setKeywordDialogCompetitor] = useState('');
  const [suggestingKeywords, setSuggestingKeywords] = useState(false);

  // Estados para edição de descrição e nome de concorrente
  const [editingCompetitorDescription, setEditingCompetitorDescription] = useState(null);
  const [competitorDescriptionValue, setCompetitorDescriptionValue] = useState('');
  const [editingCompetitorName, setEditingCompetitorName] = useState(null);
  const [newCompetitorNameValue, setNewCompetitorNameValue] = useState('');
  const [suggestingCompetitorDescription, setSuggestingCompetitorDescription] = useState(null);
  const [suggestingCompetitorProducts, setSuggestingCompetitorProducts] = useState(null);

  // Estados para configuração de keyword (requiresWith, excludeTerms)
  const [keywordOptionsDialogOpen, setKeywordOptionsDialogOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null); // { keyword, type: 'primary' | 'competitor', data }
  const [keywordRequiresWith, setKeywordRequiresWith] = useState('');
  const [keywordExcludeTerms, setKeywordExcludeTerms] = useState('');
  const [newExcludeTerm, setNewExcludeTerm] = useState('');
  const [selectedExclusionContextTags, setSelectedExclusionContextTags] = useState([]);
  const [linkWithBrand, setLinkWithBrand] = useState(false);
  const [linkAllWithBrand, setLinkAllWithBrand] = useState(false); // Para vincular todas as keywords sugeridas

  // Estados para tags (para associar keywords a tags) - MULTIPLAS TAGS
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTagToApply, setSelectedTagToApply] = useState([]); // Tags selecionadas (dialog opções) - ARRAY
  const [tagForAllKeywords, setTagForAllKeywords] = useState([]); // Tags para aplicar a todas keywords - ARRAY
  const [tagInputForAll, setTagInputForAll] = useState(''); // Input do Autocomplete de tag para todas
  const [tagInputValue, setTagInputValue] = useState(''); // Input do Autocomplete de tag individual

  // Estado para input manual de keywords por concorrente
  const [manualCompetitorKeywords, setManualCompetitorKeywords] = useState({});

  // Estados para Porta-vozes e Mensagens-Chave
  const [newSpokespersonName, setNewSpokespersonName] = useState('');
  const [newSpokespersonRole, setNewSpokespersonRole] = useState('');
  const [newKeyMessage, setNewKeyMessage] = useState('');

  const [activeTab, setActiveTab] = useState(0);
  const [briefingMenuAnchor, setBriefingMenuAnchor] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [mobileSectionDrawerOpen, setMobileSectionDrawerOpen] = useState(false);

  // Estado para dialog de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Carregar tags disponíveis
  const loadTags = useCallback(async () => {
    try {
      const response = await api.get('/tags');
      setAvailableTags(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  }, []);

  // Criar nova tag inline e adicionar automaticamente
  const handleCreateTagInline = async (tagName) => {
    if (!tagName || !tagName.trim()) return null;

    try {
      const response = await api.post('/tags', { name: tagName.trim() });
      const newTag = response.data.data;

      // Adicionar à lista de tags
      setAvailableTags(prev => [...prev, newTag]);

      // Adicionar automaticamente ao array de tags selecionadas
      setTagForAllKeywords(prev => [...prev, newTag]);

      // Dispara evento para atualizar outras páginas
      window.dispatchEvent(new CustomEvent('tags-updated'));

      setSnackbar({ open: true, message: `Tag "${newTag.name}" criada!`, severity: 'success' });
      return newTag;
    } catch (error) {
      console.error('Erro ao criar tag:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao criar tag',
        severity: 'error'
      });
      return null;
    }
  };

  // Criar nova tag inline para dialog de opções individuais
  const handleCreateTagIndividual = async (tagName) => {
    if (!tagName || !tagName.trim()) return null;

    try {
      const response = await api.post('/tags', { name: tagName.trim() });
      const newTag = response.data.data;

      // Adicionar à lista de tags
      setAvailableTags(prev => [...prev, newTag]);

      // Adicionar automaticamente ao array de tags selecionadas
      setSelectedTagToApply(prev => [...prev, newTag]);

      // Dispara evento para atualizar outras páginas
      window.dispatchEvent(new CustomEvent('tags-updated'));

      setSnackbar({ open: true, message: `Tag "${newTag.name}" criada!`, severity: 'success' });
      return newTag;
    } catch (error) {
      console.error('Erro ao criar tag:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao criar tag',
        severity: 'error'
      });
      return null;
    }
  };

  // Carregar briefing
  const loadBriefing = useCallback(async () => {
    if (!activeBrand) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/briefing');
      setBriefing(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar briefing:', error);
      setBriefing(null); // Garante que fique null se não encontrar
    } finally {
      setLoading(false);
    }
  }, [activeBrand]);

  useEffect(() => {
    loadBriefing();
    loadTags();
  }, [loadBriefing, loadTags]);

  // Sugerir descrição e keywords usando MIA
  const handleSuggestDescription = async () => {
    if (!newBriefingName.trim()) {
      setSnackbar({ open: true, message: 'Digite o nome da marca primeiro', severity: 'warning' });
      return;
    }

    try {
      setSuggestingDescription(true);
      const response = await api.post('/briefing/suggest-description', {
        brandName: newBriefingName
      });
      const { description, keywords } = response.data.data;
      setNewBriefingDescription(description);

      // Se tiver keywords sugeridas, mostrar dialog
      if (keywords && keywords.length > 0) {
        setSuggestedKeywords(keywords);
        setSelectedSuggestedKeywords(keywords); // Seleciona todas por padrão
        setKeywordDialogType('primary');
        setKeywordDialogOpen(true);
      }

      setSnackbar({ open: true, message: 'Sugestão gerada pela MIA!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao sugerir descrição:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao gerar sugestão',
        severity: 'error'
      });
    } finally {
      setSuggestingDescription(false);
    }
  };

  // Sugerir keywords para a marca (quando já tem briefing)
  const handleSuggestBrandKeywords = async () => {
    if (!briefing) return;

    try {
      setSuggestingKeywords(true);
      const response = await api.post('/briefing/suggest-description', {
        brandName: briefing.name
      });
      const { keywords } = response.data.data;

      if (keywords && keywords.length > 0) {
        // Filtrar keywords que já existem
        const existingKeywords = briefing.primaryKeywords?.map(k => k.keyword.toLowerCase()) || [];
        const newKeywords = keywords.filter(k => !existingKeywords.includes(k.toLowerCase()));

        if (newKeywords.length === 0) {
          setSnackbar({ open: true, message: 'Todas as sugestões já estão adicionadas', severity: 'info' });
          return;
        }

        setSuggestedKeywords(newKeywords);
        setSelectedSuggestedKeywords(newKeywords);
        setKeywordDialogType('primary');
        setKeywordDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Nenhuma sugestão encontrada', severity: 'info' });
      }
    } catch (error) {
      console.error('Erro ao sugerir keywords:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao gerar sugestões',
        severity: 'error'
      });
    } finally {
      setSuggestingKeywords(false);
    }
  };

  // Sugerir keywords para concorrente
  const handleSuggestCompetitorKeywords = async () => {
    if (!newCompetitorName.trim() || !briefing) {
      setSnackbar({ open: true, message: 'Digite o nome do concorrente primeiro', severity: 'warning' });
      return;
    }

    try {
      setSuggestingKeywords(true);
      const response = await api.post('/briefing/suggest-competitor-keywords', {
        competitorName: newCompetitorName
      });
      const { keywords } = response.data.data;

      if (keywords && keywords.length > 0) {
        setSuggestedKeywords(keywords);
        setSelectedSuggestedKeywords(keywords);
        setKeywordDialogType('competitor');
        setKeywordDialogCompetitor(newCompetitorName);
        setKeywordDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Nenhuma sugestão encontrada', severity: 'info' });
      }
    } catch (error) {
      console.error('Erro ao sugerir keywords de concorrente:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao gerar sugestões',
        severity: 'error'
      });
    } finally {
      setSuggestingKeywords(false);
    }
  };

  // Sugerir produtos/serviços usando MIA
  const handleSuggestProducts = async () => {
    if (!briefing) return;

    try {
      setSuggestingKeywords(true);
      const response = await api.post('/briefing/suggest-products', {
        brandName: briefing.name,
        description: briefing.description
      });
      const { products } = response.data.data;

      if (products && products.length > 0) {
        // Filtrar produtos que já existem como keywords
        const existingKeywords = briefing.primaryKeywords?.map(k => k.keyword.toLowerCase()) || [];
        const newProducts = products.filter(p => !existingKeywords.includes(p.toLowerCase()));

        if (newProducts.length === 0) {
          setSnackbar({ open: true, message: 'Todos os produtos sugeridos já estão adicionados', severity: 'info' });
          return;
        }

        setSuggestedKeywords(newProducts);
        setSelectedSuggestedKeywords(newProducts);
        setKeywordDialogType('primary');
        setKeywordDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Nenhum produto encontrado', severity: 'info' });
      }
    } catch (error) {
      console.error('Erro ao sugerir produtos:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao gerar sugestões de produtos',
        severity: 'error'
      });
    } finally {
      setSuggestingKeywords(false);
    }
  };

  // Sugerir produtos/serviços de concorrente usando MIA
  const handleSuggestCompetitorProducts = async (competitorName, description) => {
    if (!briefing) return;

    try {
      setSuggestingCompetitorProducts(competitorName);
      const response = await api.post('/briefing/suggest-competitor-products', {
        competitorName,
        description
      });
      const { products } = response.data.data;

      if (products && products.length > 0) {
        // Filtrar produtos que já existem como keywords do concorrente
        const existingKeywords = briefing.competitorKeywords
          ?.filter(k => k.competitor.toLowerCase() === competitorName.toLowerCase())
          .map(k => k.keyword.toLowerCase()) || [];
        const newProducts = products.filter(p => !existingKeywords.includes(p.toLowerCase()));

        if (newProducts.length === 0) {
          setSnackbar({ open: true, message: 'Todos os produtos sugeridos já estão adicionados', severity: 'info' });
          return;
        }

        setSuggestedKeywords(newProducts);
        setSelectedSuggestedKeywords(newProducts);
        setKeywordDialogType('competitor');
        setKeywordDialogCompetitor(competitorName);
        setKeywordDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Nenhum produto encontrado', severity: 'info' });
      }
    } catch (error) {
      console.error('Erro ao sugerir produtos do concorrente:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao gerar sugestões de produtos',
        severity: 'error'
      });
    } finally {
      setSuggestingCompetitorProducts(null);
    }
  };

  // Adicionar keyword manual a concorrente existente
  const handleAddManualCompetitorKeyword = async (competitorName) => {
    const keyword = manualCompetitorKeywords[competitorName]?.trim();
    if (!keyword || !briefing) return;

    try {
      setSaving(true);
      await api.post(`/briefing/${briefing._id}/competitor-keyword`, {
        keyword: keyword.toLowerCase(),
        competitor: competitorName
      });

      // Limpar o input
      setManualCompetitorKeywords(prev => ({
        ...prev,
        [competitorName]: ''
      }));

      await loadBriefing();
      setSnackbar({ open: true, message: 'Keyword adicionada!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao adicionar keyword:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao adicionar keyword',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Sugerir mais keywords para concorrente existente
  const handleSuggestMoreCompetitorKeywords = async (competitorName) => {
    if (!briefing) return;

    try {
      setSuggestingKeywords(true);
      const response = await api.post('/briefing/suggest-competitor-keywords', {
        competitorName
      });
      const { keywords } = response.data.data;

      if (keywords && keywords.length > 0) {
        // Filtrar keywords que já existem
        const existingKeywords = briefing.competitorKeywords
          ?.filter(k => k.competitor.toLowerCase() === competitorName.toLowerCase())
          .map(k => k.keyword.toLowerCase()) || [];
        const newKeywords = keywords.filter(k => !existingKeywords.includes(k.toLowerCase()));

        if (newKeywords.length === 0) {
          setSnackbar({ open: true, message: 'Todas as sugestões já estão adicionadas', severity: 'info' });
          return;
        }

        setSuggestedKeywords(newKeywords);
        setSelectedSuggestedKeywords(newKeywords);
        setKeywordDialogType('competitor');
        setKeywordDialogCompetitor(competitorName);
        setKeywordDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Nenhuma sugestão encontrada', severity: 'info' });
      }
    } catch (error) {
      console.error('Erro ao sugerir mais keywords:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao gerar sugestões',
        severity: 'error'
      });
    } finally {
      setSuggestingKeywords(false);
    }
  };

  // Sugerir descrição de concorrente
  const handleSuggestCompetitorDescription = async (competitorName) => {
    if (!briefing) return;

    try {
      setSuggestingCompetitorDescription(competitorName);
      const response = await api.post('/briefing/suggest-competitor-description', {
        competitorName
      });
      const { description } = response.data.data;

      if (description) {
        // Salvar a descrição automaticamente
        await api.put(`/briefing/${briefing._id}/competitor/${encodeURIComponent(competitorName)}/description`, {
          description
        });
        await loadBriefing();
        setSnackbar({ open: true, message: 'Descrição gerada pela MIA!', severity: 'success' });
      }
    } catch (error) {
      console.error('Erro ao sugerir descrição do concorrente:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao gerar descrição',
        severity: 'error'
      });
    } finally {
      setSuggestingCompetitorDescription(null);
    }
  };

  // Salvar descrição de concorrente editada
  const handleSaveCompetitorDescription = async (competitorName) => {
    if (!briefing) return;

    try {
      setSaving(true);
      await api.put(`/briefing/${briefing._id}/competitor/${encodeURIComponent(competitorName)}/description`, {
        description: competitorDescriptionValue
      });
      await loadBriefing();
      setEditingCompetitorDescription(null);
      setCompetitorDescriptionValue('');
      setSnackbar({ open: true, message: 'Descrição salva!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao salvar descrição:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar descrição', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Salvar nome de concorrente editado
  const handleSaveCompetitorName = async (oldName) => {
    if (!briefing || !newCompetitorNameValue.trim()) return;

    // Previne salvar se não mudou
    if (oldName === newCompetitorNameValue.trim()) {
      setEditingCompetitorName(null);
      return;
    }

    try {
      setSaving(true);
      await api.put(`/briefing/${briefing._id}/competitor/${encodeURIComponent(oldName)}/name`, {
        newName: newCompetitorNameValue.trim()
      });
      await loadBriefing();
      setEditingCompetitorName(null);
      setNewCompetitorNameValue('');
      setSnackbar({ open: true, message: 'Nome do concorrente atualizado!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao alterar nome do concorrente:', error);
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao alterar nome do concorrente', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Abrir dialog de opções de keyword
  const handleOpenKeywordOptions = (keyword, type, keywordData = null) => {
    // Buscar dados da keyword
    let data;
    if (type === 'primary') {
      data = briefing?.primaryKeywords?.find(k => k.keyword === keyword);
    } else {
      data = briefing?.competitorKeywords?.find(k => k.keyword === keyword);
    }

    // Determinar o nome da marca/concorrente para vincular
    const brandNameLower = briefing?.name?.toLowerCase() || '';
    const requiresWithValue = data?.requiresWith || '';

    // Verifica se está vinculado com a marca ou concorrente
    let isLinkedWithBrand = false;
    if (type === 'primary') {
      isLinkedWithBrand = requiresWithValue === brandNameLower;
    } else {
      // Para concorrente, verifica se está vinculado ao nome do concorrente
      const competitorData = briefing?.competitorKeywords?.find(k => k.keyword === keyword);
      const competitorNameLower = competitorData?.competitor?.toLowerCase() || '';
      isLinkedWithBrand = requiresWithValue === competitorNameLower;
    }

    setEditingKeyword({ keyword, type, data: data || keywordData });
    setKeywordRequiresWith(requiresWithValue);
    setLinkWithBrand(isLinkedWithBrand);
    setKeywordExcludeTerms('');
    setNewExcludeTerm('');
    // Carregar tags existentes - converter IDs para objetos
    const existingTagIds = data?.tagsToApply || [];
    const existingTags = availableTags.filter(tag => existingTagIds.includes(tag._id));
    setSelectedTagToApply(existingTags); // Array de objetos de tag
    const exclusionContextTagIds = data?.exclusionContextTags || [];
    setSelectedExclusionContextTags(availableTags.filter(tag => exclusionContextTagIds.includes(tag._id)));
    setTagInputValue(''); // Limpar input do Autocomplete de tag
    setKeywordOptionsDialogOpen(true);
  };

  // Adicionar termo de exclusão
  const handleAddExcludeTerm = () => {
    if (!newExcludeTerm.trim()) return;

    const currentTerms = editingKeyword?.data?.excludeTerms || [];
    if (currentTerms.includes(newExcludeTerm.toLowerCase().trim())) {
      setSnackbar({ open: true, message: 'Termo já adicionado', severity: 'warning' });
      return;
    }

    setEditingKeyword(prev => ({
      ...prev,
      data: {
        ...prev.data,
        excludeTerms: [...currentTerms, newExcludeTerm.toLowerCase().trim()]
      }
    }));
    setNewExcludeTerm('');
  };

  // Remover termo de exclusão
  const handleRemoveExcludeTerm = (term) => {
    setEditingKeyword(prev => ({
      ...prev,
      data: {
        ...prev.data,
        excludeTerms: (prev.data?.excludeTerms || []).filter(t => t !== term)
      }
    }));
  };

  // Salvar opções de keyword
  const handleCreateExclusionContextTag = async (tagName) => {
    const newTag = await handleCreateTagInline(tagName);
    if (newTag) {
      setSelectedExclusionContextTags(prev => [...prev, newTag]);
    }
  };

  const handleSaveKeywordOptions = async () => {
    if (!briefing || !editingKeyword) return;

    try {
      setSaving(true);
      const endpoint = editingKeyword.type === 'primary'
        ? `/briefing/${briefing._id}/primary-keyword/${encodeURIComponent(editingKeyword.keyword)}/options`
        : `/briefing/${briefing._id}/competitor-keyword/${encodeURIComponent(editingKeyword.keyword)}/options`;

      // Determina o valor de requiresWith baseado no checkbox
      let finalRequiresWith = keywordRequiresWith.trim() || null;
      if (linkWithBrand) {
        if (editingKeyword.type === 'primary') {
          finalRequiresWith = briefing.name.toLowerCase();
        } else {
          // Para concorrente, usa o nome do concorrente
          const competitorData = briefing.competitorKeywords?.find(k => k.keyword === editingKeyword.keyword);
          finalRequiresWith = competitorData?.competitor?.toLowerCase() || null;
        }
      }

      await api.put(endpoint, {
        requiresWith: finalRequiresWith,
        excludeTerms: editingKeyword.data?.excludeTerms || [],
        tagsToApply: selectedTagToApply.map(tag => tag._id),
        exclusionContextTags: selectedExclusionContextTags.map(tag => tag._id)
      });

      await loadBriefing();
      setKeywordOptionsDialogOpen(false);
      setEditingKeyword(null);
      setLinkWithBrand(false);
      setSelectedTagToApply([]);
      setSelectedExclusionContextTags([]);
      setSnackbar({ open: true, message: 'Opções salvas!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao salvar opções:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar opções', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Agrupa concorrentes por nome (com descrição)
  const getGroupedCompetitors = () => {
    if (!briefing?.competitorKeywords) return {};

    return briefing.competitorKeywords.reduce((acc, kw) => {
      const name = kw.competitor;
      if (!acc[name]) {
        acc[name] = {
          keywords: [],
          keywordsData: [],
          description: kw.competitorDescription || ''
        };
      }
      acc[name].keywords.push(kw.keyword);
      acc[name].keywordsData.push(kw);
      // Pega a primeira descrição encontrada
      if (kw.competitorDescription && !acc[name].description) {
        acc[name].description = kw.competitorDescription;
      }
      return acc;
    }, {});
  };

  // Toggle seleção de keyword sugerida
  const toggleKeywordSelection = (keyword) => {
    setSelectedSuggestedKeywords(prev =>
      prev.includes(keyword)
        ? prev.filter(k => k !== keyword)
        : [...prev, keyword]
    );
  };

  // Estado para guardar keywords selecionadas durante criação
  const [pendingKeywords, setPendingKeywords] = useState([]);

  // Adicionar keywords selecionadas
  const handleAddSuggestedKeywords = async () => {
    if (selectedSuggestedKeywords.length === 0) {
      setKeywordDialogOpen(false);
      return;
    }

    // Se não tem briefing ainda (está criando), guardar para usar na criação
    if (!briefing) {
      setPendingKeywords(selectedSuggestedKeywords);
      setKeywordDialogOpen(false);
      setSnackbar({
        open: true,
        message: `${selectedSuggestedKeywords.length} keywords serão adicionadas ao criar o briefing`,
        severity: 'info'
      });
      return;
    }

    try {
      setSaving(true);

      if (keywordDialogType === 'primary') {
        // Adicionar keywords primárias uma a uma
        // Se linkAllWithBrand está ativo, vincula com o nome da marca
        const requiresWithValue = linkAllWithBrand ? briefing.name?.toLowerCase() : undefined;

        for (const keyword of selectedSuggestedKeywords) {
          try {
            await api.post(`/briefing/${briefing._id}/primary-keyword`, {
              keyword: keyword.toLowerCase().trim(),
              weight: 5,
              requiresWith: requiresWithValue,
              tagsToApply: tagForAllKeywords.map(tag => tag._id)
            });
          } catch (err) {
            // Ignora erros de keyword duplicada
            if (!err.response?.data?.message?.includes('já existe')) {
              console.error('Erro ao adicionar keyword:', keyword, err);
            }
          }
        }
      } else {
        // Adicionar keywords de concorrente uma a uma
        // Se linkAllWithBrand está ativo, vincula com o nome do concorrente
        const requiresWithValue = linkAllWithBrand ? keywordDialogCompetitor?.toLowerCase() : undefined;

        for (const keyword of selectedSuggestedKeywords) {
          try {
            await api.post(`/briefing/${briefing._id}/competitor-keyword`, {
              keyword: keyword.toLowerCase().trim(),
              competitor: keywordDialogCompetitor,
              requiresWith: requiresWithValue,
              tagsToApply: tagForAllKeywords.map(tag => tag._id)
            });
          } catch (err) {
            if (!err.response?.data?.message?.includes('já existe')) {
              console.error('Erro ao adicionar keyword de concorrente:', keyword, err);
            }
          }
        }
        setNewCompetitorName('');
        setNewCompetitorKeyword('');
      }

      // Recarregar briefing
      await loadBriefing();
      setKeywordDialogOpen(false);
      setLinkAllWithBrand(false); // Reset o estado
      setTagForAllKeywords([]); // Reset a tag
      setSnackbar({
        open: true,
        message: `${selectedSuggestedKeywords.length} palavra(s)-chave adicionada(s)${linkAllWithBrand ? ' (vinculadas)' : ''}${tagForAllKeywords ? ' com tag' : ''}!`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao adicionar keywords:', error);
      setSnackbar({ open: true, message: 'Erro ao adicionar palavras-chave', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Criar novo briefing
  const handleCreateBriefing = async () => {
    if (!newBriefingName.trim()) {
      setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'warning' });
      return;
    }

    try {
      setSaving(true);

      // Monta lista de keywords: nome da marca + keywords pendentes da MIA
      const keywordsToAdd = [
        { keyword: newBriefingName.toLowerCase(), weight: 10 },
        ...pendingKeywords.map(kw => ({ keyword: kw.toLowerCase(), weight: 5 }))
      ];

      const response = await api.post('/briefing', {
        name: newBriefingName,
        description: newBriefingDescription,
        primaryKeywords: keywordsToAdd
      });
      setBriefing(response.data.data);
      setIsCreating(false);
      setNewBriefingName('');
      setNewBriefingDescription('');
      setPendingKeywords([]);
      setSnackbar({
        open: true,
        message: pendingKeywords.length > 0
          ? `Briefing criado com ${keywordsToAdd.length} palavras-chave!`
          : 'Briefing criado com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao criar briefing:', error);
      setSnackbar({ open: true, message: 'Erro ao criar briefing', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal de exclusão
  const handleDeleteBriefing = () => {
    setDeleteDialogOpen(true);
  };

  // Confirmar exclusão
  const confirmDeleteBriefing = async () => {
    if (!briefing) return;

    try {
      setSaving(true);
      await api.delete(`/briefing/${briefing._id}`);
      setBriefing(null);
      setIsCreating(false);
      setDeleteDialogOpen(false);
      setSnackbar({ open: true, message: 'Briefing excluído com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao excluir briefing:', error);
      setSnackbar({ open: true, message: 'Erro ao excluir briefing', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Adicionar keyword primária
  const handleAddPrimaryKeyword = async () => {
    if (!newPrimaryKeyword.trim() || !briefing) return;

    try {
      setSaving(true);
      const response = await api.post(`/briefing/${briefing._id}/primary-keyword`, {
        keyword: newPrimaryKeyword.toLowerCase().trim(),
        weight: 5
      });
      setBriefing(response.data.data);
      setNewPrimaryKeyword('');
      setSnackbar({ open: true, message: 'Palavra-chave adicionada!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao adicionar keyword:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao adicionar palavra-chave',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Remover keyword primária
  const handleRemovePrimaryKeyword = async (keyword) => {
    if (!briefing) return;

    try {
      setSaving(true);
      const response = await api.delete(
        `/briefing/${briefing._id}/primary-keyword/${encodeURIComponent(keyword)}`
      );
      setBriefing(response.data.data);
      setSnackbar({ open: true, message: 'Palavra-chave removida', severity: 'success' });
    } catch (error) {
      console.error('Erro ao remover keyword:', error);
      setSnackbar({ open: true, message: 'Erro ao remover palavra-chave', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Adicionar keyword de concorrente
  const handleAddCompetitorKeyword = async () => {
    if (!newCompetitorKeyword.trim() || !newCompetitorName.trim() || !briefing) return;

    try {
      setSaving(true);
      const response = await api.post(`/briefing/${briefing._id}/competitor-keyword`, {
        keyword: newCompetitorKeyword.toLowerCase().trim(),
        competitor: newCompetitorName.trim()
      });
      setBriefing(response.data.data);
      setNewCompetitorKeyword('');
      setNewCompetitorName('');
      setSnackbar({ open: true, message: 'Concorrente adicionado!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao adicionar concorrente:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao adicionar concorrente',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Remover keyword de concorrente
  const handleRemoveCompetitorKeyword = async (keyword) => {
    if (!briefing) return;

    try {
      setSaving(true);
      const response = await api.delete(
        `/briefing/${briefing._id}/competitor-keyword/${encodeURIComponent(keyword)}`
      );
      setBriefing(response.data.data);
      setSnackbar({ open: true, message: 'Concorrente removido', severity: 'success' });
    } catch (error) {
      console.error('Erro ao remover concorrente:', error);
      setSnackbar({ open: true, message: 'Erro ao remover concorrente', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Atualizar configurações
  const handleUpdateSettings = async (settings) => {
    if (!briefing) return;

    try {
      setSaving(true);
      const response = await api.put(`/briefing/${briefing._id}`, { settings });
      setBriefing(response.data.data);
      setSnackbar({ open: true, message: 'Configurações salvas!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar configurações', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Atualizar info principal (Nome e Descrição)
  const handleUpdateBriefingInfo = async () => {
    if (!briefing || !editingBriefingName.trim()) return;

    try {
      setSaving(true);
      const response = await api.put(`/briefing/${briefing._id}`, {
        name: editingBriefingName,
        description: editingBriefingDescription
      });
      setBriefing(response.data.data);
      setEditInfoOpen(false);
      setSnackbar({ open: true, message: 'Briefing atualizado com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao editar briefing:', error);
      setSnackbar({ open: true, message: 'Erro ao editar briefing', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Gerenciamento de Porta-vozes
  const handleAddSpokesperson = async () => {
    if (!newSpokespersonName.trim() || !briefing) return;

    try {
      setSaving(true);
      const updatedSpokespeople = [...(briefing.spokespeople || []), { 
        name: newSpokespersonName.trim(), 
        role: newSpokespersonRole.trim() 
      }];
      
      const response = await api.put(`/briefing/${briefing._id}`, {
        spokespeople: updatedSpokespeople
      });
      
      setBriefing(response.data.data);
      setNewSpokespersonName('');
      setNewSpokespersonRole('');
      setSnackbar({ open: true, message: 'Porta-voz adicionado!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao adicionar porta-voz:', error);
      setSnackbar({ open: true, message: 'Erro ao adicionar porta-voz', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSpokesperson = async (name) => {
    if (!briefing) return;

    try {
      setSaving(true);
      const updatedSpokespeople = (briefing.spokespeople || []).filter(s => s.name !== name);
      
      const response = await api.put(`/briefing/${briefing._id}`, {
        spokespeople: updatedSpokespeople
      });
      
      setBriefing(response.data.data);
      setSnackbar({ open: true, message: 'Porta-voz removido', severity: 'success' });
    } catch (error) {
      console.error('Erro ao remover porta-voz:', error);
      setSnackbar({ open: true, message: 'Erro ao remover porta-voz', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Gerenciamento de Mensagens-Chave
  const handleAddKeyMessage = async () => {
    if (!newKeyMessage.trim() || !briefing) return;

    try {
      setSaving(true);
      const updatedMessages = [...(briefing.keyMessages || []), { 
        message: newKeyMessage.trim() 
      }];
      
      const response = await api.put(`/briefing/${briefing._id}`, {
        keyMessages: updatedMessages
      });
      
      setBriefing(response.data.data);
      setNewKeyMessage('');
      setSnackbar({ open: true, message: 'Mensagem-chave adicionada!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao adicionar mensagem-chave:', error);
      setSnackbar({ open: true, message: 'Erro ao adicionar mensagem-chave', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveKeyMessage = async (message) => {
    if (!briefing) return;

    try {
      setSaving(true);
      const updatedMessages = (briefing.keyMessages || []).filter(m => m.message !== message);
      
      const response = await api.put(`/briefing/${briefing._id}`, {
        keyMessages: updatedMessages
      });
      
      setBriefing(response.data.data);
      setSnackbar({ open: true, message: 'Mensagem-chave removida', severity: 'success' });
    } catch (error) {
      console.error('Erro ao remover mensagem-chave:', error);
      setSnackbar({ open: true, message: 'Erro ao remover mensagem-chave', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Sugerir descrição ao editar
  const handleSuggestEditDescription = async () => {
    if (!editingBriefingName.trim()) return;

    try {
      setSuggestingDescription(true);
      const response = await api.post('/briefing/suggest-description', {
        brandName: editingBriefingName
      });

      if (response.data.data.description) {
        setEditingBriefingDescription(response.data.data.description);
        setSnackbar({ open: true, message: 'Nova descrição sugerida com IA!', severity: 'success' });
      }
    } catch (error) {
      console.error('Erro ao sugerir descrição com IA:', error);
      setSnackbar({ open: true, message: 'Erro ao sugerir com IA', severity: 'error' });
    } finally {
      setSuggestingDescription(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Sem briefing - mostrar criação
  if (!briefing) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LightbulbIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Configure seu Briefing
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            O briefing contém as palavras-chave da sua marca e concorrentes.
            Ele é usado para analisar a exposição nos artigos.
          </Typography>

          {!isCreating ? (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setIsCreating(true)}
            >
              Criar Briefing
            </Button>
          ) : (
            <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'left' }}>
              <TextField
                fullWidth
                label="Nome da Marca"
                placeholder="Ex: Mensure"
                value={newBriefingName}
                onChange={(e) => setNewBriefingName(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
              />
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Descrição (opcional)
                  </Typography>
                  <Tooltip title="Pedir sugestão para MIA">
                    <Button
                      size="small"
                      startIcon={suggestingDescription ? <CircularProgress size={14} /> : <MIAIcon />}
                      onClick={handleSuggestDescription}
                      disabled={suggestingDescription || !newBriefingName.trim()}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      {suggestingDescription ? 'Gerando...' : 'Sugerir com MIA'}
                    </Button>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  placeholder="Breve descrição da marca para contexto da IA"
                  value={newBriefingDescription}
                  onChange={(e) => setNewBriefingDescription(e.target.value)}
                  multiline
                  rows={2}
                />
              </Box>

              {/* Mostrar keywords pendentes */}
              {pendingKeywords.length > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                  <Typography variant="caption" color="success.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <MIAIcon fontSize="small" />
                    {pendingKeywords.length} palavras-chave sugeridas pela MIA:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {pendingKeywords.map((kw, idx) => (
                      <Chip
                        key={idx}
                        label={kw}
                        size="small"
                        color="success"
                        variant="outlined"
                        onDelete={() => setPendingKeywords(prev => prev.filter((_, i) => i !== idx))}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button onClick={() => { setIsCreating(false); setPendingKeywords([]); }}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateBriefing}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                  Criar
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    );
  }

  // Com briefing - mostrar edição
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2.5, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, fontSize: { xs: '1.45rem', md: '2.125rem' }, lineHeight: 1.15, fontWeight: { xs: 800, md: 400 } }}>
              <BriefingIcon sx={{ fontSize: { xs: 28, md: 36 }, flexShrink: 0 }} color="primary" />
              Briefing{isMobile ? ' — ' : ': '}{briefing.name}
            </Typography>
            <Tooltip title="Editar Nome e Descrição">
              <IconButton 
                size="small" 
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                onClick={() => {
                  setEditingBriefingName(briefing.name);
                  setEditingBriefingDescription(briefing.description || '');
                  setEditInfoOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {briefing.description && (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.82rem', md: '1rem' }, lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: { xs: descriptionExpanded ? 'unset' : 3, md: 'unset' }, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {briefing.description}
              </Typography>
              {isMobile && briefing.description.length > 130 && (
                <Button size="small" onClick={() => setDescriptionExpanded(value => !value)} sx={{ px: 0, minWidth: 0, textTransform: 'none', fontSize: '0.72rem' }}>
                  {descriptionExpanded ? 'Ver menos' : 'Ver mais'}
                </Button>
              )}
            </>
          )}
        </Box>
        <IconButton onClick={(event) => setBriefingMenuAnchor(event.currentTarget)} sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }} aria-label="Ações do briefing">
          <MoreVertIcon />
        </IconButton>
        <Button
          onClick={handleDeleteBriefing}
          disabled={saving}
          sx={{ 
            display: { xs: 'none', md: 'inline-flex' },
            whiteSpace: 'nowrap', 
            borderRadius: '8px', 
            color: '#f87171',
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 12px',
            '&:hover': { 
              bgcolor: 'rgba(248, 113, 113, 0.08)' 
            } 
          }}
        >
          <DeleteIcon sx={{ fontSize: 20, mr: 1 }} />
          Excluir Briefing
        </Button>
        <Menu anchorEl={briefingMenuAnchor} open={Boolean(briefingMenuAnchor)} onClose={() => setBriefingMenuAnchor(null)}>
          <MenuItem onClick={() => { setBriefingMenuAnchor(null); setEditingBriefingName(briefing.name); setEditingBriefingDescription(briefing.description || ''); setEditInfoOpen(true); }}>
            <EditIcon fontSize="small" sx={{ mr: 1.25 }} /> Editar briefing
          </MenuItem>
          <MenuItem onClick={() => { setBriefingMenuAnchor(null); handleDeleteBriefing(); }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1.25 }} /> Excluir briefing
          </MenuItem>
        </Menu>
      </Box>
      
      {/* Sistema de Abas Estratégico */}
      {isMobile && (
        <Button fullWidth variant="outlined" onClick={(event) => { event.currentTarget.blur(); setMobileSectionDrawerOpen(true); }} endIcon={<ExpandMoreIcon />} sx={{ mb: 2, minHeight: 46, justifyContent: 'space-between', px: 1.5, textTransform: 'none', fontWeight: 800 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeTab === 0 ? <StarIcon sx={{ fontSize: 18 }} /> : activeTab === 1 ? <GroupsIcon sx={{ fontSize: 18 }} /> : <SettingsIcon sx={{ fontSize: 18 }} />}
            {activeTab === 0 ? 'Minha Marca' : activeTab === 1 ? 'Concorrentes' : 'Configurações'}
          </Box>
        </Button>
      )}

      <Drawer anchor="bottom" open={isMobile && mobileSectionDrawerOpen} onClose={() => setMobileSectionDrawerOpen(false)} ModalProps={{ disableRestoreFocus: true }} PaperProps={{ sx: { borderRadius: '18px 18px 0 0', px: 2, pt: 1, pb: 'calc(20px + env(safe-area-inset-bottom, 0px))' } }}>
        <BottomSheetHandle sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box><Typography sx={{ fontSize: '1rem', fontWeight: 850 }}>Seção do Briefing</Typography><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Escolha o conteúdo que deseja gerenciar</Typography></Box>
          <IconButton onClick={() => setMobileSectionDrawerOpen(false)} aria-label="Fechar seções do briefing"><CloseIcon /></IconButton>
        </Box>
        <List disablePadding>
          {[
            { label: 'Minha Marca', icon: <StarIcon />, description: `${briefing.primaryKeywords?.length || 0} palavras-chave` },
            { label: 'Concorrentes', icon: <GroupsIcon />, description: `${Object.keys(briefing.competitors || {}).length} concorrentes` },
            { label: 'Configurações', icon: <SettingsIcon />, description: 'Regras e automações' }
          ].map((section, index) => (
            <ListItemButton key={section.label} selected={activeTab === index} onClick={(event) => { event.currentTarget.blur(); setActiveTab(index); setMobileSectionDrawerOpen(false); }} sx={{ minHeight: 54, borderRadius: 2, mb: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 38, color: activeTab === index ? 'primary.main' : 'text.secondary' }}>{section.icon}</ListItemIcon>
              <ListItemText primary={section.label} secondary={section.description} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: activeTab === index ? 800 : 650 }} secondaryTypographyProps={{ fontSize: '0.68rem' }} />
              {activeTab === index && <CheckIcon color="primary" />}
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box sx={{ display: { xs: 'none', md: 'block' }, borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant={isMobile ? 'fullWidth' : 'scrollable'}
          scrollButtons={isMobile ? false : 'auto'}
          sx={{
            '& .MuiTab-root': { 
              fontWeight: 700, 
              fontSize: { xs: '0.72rem', md: '0.9rem' },
              textTransform: 'none',
              minWidth: { xs: 0, md: 120 },
              px: { xs: 0.5, md: 2 },
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main'
              }
            }
          }}
        >
          <Tab icon={<StarIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Minha Marca" />
          <Tab icon={<GroupsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Concorrentes" />
          <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Configurações" />
        </Tabs>
      </Box>

      {isMobile && activeTab === 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', mb: 2, pb: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          <Chip size="small" label={`${briefing.primaryKeywords?.length || 0} palavras-chave`} />
          <Chip size="small" label={`${briefing.spokespeople?.length || 0} porta-vozes`} />
          <Chip size="small" label={`${briefing.keyMessages?.length || 0} mensagens-chave`} />
        </Box>
      )}

      <Grid container spacing={{ xs: 0, md: 3 }} sx={{ rowGap: { xs: 1.5, md: 0 } }}>
        {activeTab === 0 && (
          <>
            {/* Palavras-chave da Marca */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 1.5, md: 2.5 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon color="success" />
                    Palavras-chave da Marca
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Sugerir palavras-chave gerais">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={suggestingKeywords ? <CircularProgress size={14} /> : <MIAIcon />}
                        onClick={handleSuggestBrandKeywords}
                        disabled={suggestingKeywords}
                        sx={{ textTransform: 'none', fontSize: '0.725rem', borderRadius: 2 }}
                      >
                        Keywords
                      </Button>
                    </Tooltip>
                    <Tooltip title="Listar produtos/serviços da marca">
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={suggestingKeywords ? <CircularProgress size={14} /> : <ProductIcon />}
                        onClick={handleSuggestProducts}
                        disabled={suggestingKeywords}
                        sx={{ textTransform: 'none', fontSize: '0.725rem', borderRadius: 2 }}
                      >
                        Produtos
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Termos que identificam sua marca. Quanto mais vezes aparecerem, maior a exposição.
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {briefing.primaryKeywords?.map((kw, index) => {
                    const hasExclusionContextTags = kw.exclusionContextTags?.length > 0;
                    const hasOptions = kw.requiresWith || (kw.excludeTerms && kw.excludeTerms.length > 0) || hasExclusionContextTags;
                    return (
                      <Tooltip
                        key={index}
                        arrow
                        placement="top"
                        title={
                          hasOptions || kw.tagsToApply?.length > 0 ? (
                            <Box>
                              {kw.requiresWith && <Typography variant="caption">Requer: {kw.requiresWith}</Typography>}
                              {kw.excludeTerms?.length > 0 && (
                                <Typography variant="caption" display="block">
                                  Excluir: {kw.excludeTerms.join(', ')}
                                </Typography>
                              )}
                              {kw.tagsToApply?.length > 0 && (
                                <Typography variant="caption" display="block" sx={{ color: 'primary.light' }}>
                                  {kw.tagsToApply.length} tag(s): {availableTags.filter(t => kw.tagsToApply.includes(t._id)).map(t => t.name).join(', ')}
                                </Typography>
                              )}
                            </Box>
                          ) : 'Clique para configurar'
                        }
                      >
                        <Chip
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {kw.keyword}
                              {kw.requiresWith && <LinkIcon sx={{ fontSize: 12, opacity: 0.7 }} />}
                              {kw.excludeTerms?.length > 0 && <BlockIcon sx={{ fontSize: 12, opacity: 0.7 }} />}
                              {kw.tagsToApply?.length > 0 && (
                                <TagIcon sx={{ fontSize: 12, opacity: 0.8, color: 'primary.main' }} />
                              )}
                              {hasExclusionContextTags && <TagIcon sx={{ fontSize: 12, opacity: 0.8, color: 'warning.main' }} />}
                            </Box>
                          }
                          onClick={() => handleOpenKeywordOptions(kw.keyword, 'primary')}
                          onDelete={() => handleRemovePrimaryKeyword(kw.keyword)}
                          color="success"
                          variant={hasOptions ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer', borderRadius: '8px', fontSize: '0.825rem' }}
                        />
                      </Tooltip>
                    );
                  })}
                  {briefing.primaryKeywords?.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma palavra-chave adicionada
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Nova palavra-chave"
                    value={newPrimaryKeyword}
                    onChange={(e) => setNewPrimaryKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddPrimaryKeyword()}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleAddPrimaryKeyword}
                    disabled={saving || !newPrimaryKeyword.trim()}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Inteligência Qualitativa */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: { xs: 1.5, md: 2.5 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }
                }}
              >
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MIAIcon color="primary" />
                  Inteligência Qualitativa (SMS)
                </Typography>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontWeight: 700 }}>
                    <PersonIcon color="primary" /> Porta-vozes
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField 
                      size="small" 
                      placeholder="Nome" 
                      value={newSpokespersonName} 
                      onChange={(e) => setNewSpokespersonName(e.target.value)} 
                      sx={{ flex: 2 }}
                    />
                    <TextField 
                      size="small" 
                      placeholder="Cargo (opcional)" 
                      value={newSpokespersonRole} 
                      onChange={(e) => setNewSpokespersonRole(e.target.value)} 
                      sx={{ flex: 1.5 }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleAddSpokesperson} 
                      disabled={saving || !newSpokespersonName.trim()}
                      sx={{ borderRadius: 2, minWidth: 48 }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {briefing.spokespeople?.map((sp, idx) => (
                      <Chip 
                        key={idx} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{sp.name}</Typography>
                            {sp.role && <Typography variant="caption" sx={{ opacity: 0.8 }}>⬢ {sp.role}</Typography>}
                          </Box>
                        } 
                        onDelete={() => handleRemoveSpokesperson(sp.name)} 
                        color="primary" 
                        variant="outlined"
                        sx={{ borderRadius: 2, py: 2 }}
                      />
                    ))}
                    {(!briefing.spokespeople || briefing.spokespeople.length === 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                        Nenhum porta-voz cadastrado
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, fontWeight: 700 }}>
                    <MessageIcon color="secondary" /> Mensagens-Chave
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField 
                      size="small" 
                      placeholder="Ex: Sustentabilidade é o pilar da marca..." 
                      value={newKeyMessage} 
                      onChange={(e) => setNewKeyMessage(e.target.value)} 
                      fullWidth 
                    />
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      onClick={handleAddKeyMessage} 
                      disabled={saving || !newKeyMessage.trim()}
                      sx={{ borderRadius: 2, minWidth: 48 }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {briefing.keyMessages?.map((km, idx) => (
                      <Chip 
                        key={idx} 
                        label={km.message} 
                        onDelete={() => handleRemoveKeyMessage(km.message)} 
                        color="secondary" 
                        variant="outlined"
                        sx={{ 
                          borderRadius: 2, 
                          py: 2,
                          maxWidth: '100%',
                          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 }
                        }} 
                      />
                    ))}
                    {(!briefing.keyMessages || briefing.keyMessages.length === 0) && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
                        Nenhuma mensagem cadastrada
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </>
        )}

        {/* Keywords de Concorrentes */}
        {/* Keywords de Concorrentes */}
        {activeTab === 1 && (
          <Grid item xs={12} sx={{ px: { xs: '0 !important', md: '12px !important' } }}>
            <Card sx={{ background: 'transparent', boxShadow: 'none', border: 'none', backgroundImage: 'none' }}>
              <CardContent sx={{ p: { xs: 0, md: 2 }, '&:last-child': { pb: { xs: 0, md: 2 } } }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <GroupsIcon color="warning" />
                  Concorrentes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Marcas concorrentes. Se aparecerem junto com a sua, a exposição será "Compartilhada".
                </Typography>

                {/* Lista de concorrentes agrupados */}
                <Box sx={{ mb: 2 }}>
                  {Object.keys(getGroupedCompetitors()).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nenhum concorrente adicionado
                    </Typography>
                  ) : (
                    Object.entries(getGroupedCompetitors()).map(([competitorName, data]) => (
                      <Box
                        key={competitorName}
                        sx={{
                          p: { xs: 1.5, md: 2.5 },
                          mb: 2,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                          transition: 'all 0.2s',
                          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }
                        }}
                      >
                        {/* Cabeçalho do concorrente */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          {editingCompetitorName === competitorName ? (
                            <Box sx={{ display: 'flex', gap: 1, flex: 1, mr: 2 }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={newCompetitorNameValue}
                                onChange={(e) => setNewCompetitorNameValue(e.target.value)}
                                placeholder="Novo nome do concorrente..."
                                autoFocus
                                onKeyPress={(e) => e.key === 'Enter' && handleSaveCompetitorName(competitorName)}
                              />
                              <Button size="small" onClick={() => setEditingCompetitorName(null)}>Cancelar</Button>
                              <Button size="small" variant="contained" color="warning" onClick={() => handleSaveCompetitorName(competitorName)} disabled={saving}>Salvar</Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body1" sx={{ fontWeight: 700, color: 'warning.main', letterSpacing: '0.02em' }}>
                                {competitorName}
                              </Typography>
                              <Tooltip title="Editar nome">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingCompetitorName(competitorName);
                                    setNewCompetitorNameValue(competitorName);
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {data.keywords.length} keyword{data.keywords.length > 1 ? 's' : ''}
                          </Typography>
                        </Box>

                        {/* Descrição do concorrente */}
                        {editingCompetitorDescription === competitorName ? (
                          <Box sx={{ mb: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              rows={2}
                              value={competitorDescriptionValue}
                              onChange={(e) => setCompetitorDescriptionValue(e.target.value)}
                              placeholder="Descrição do concorrente..."
                              sx={{ mb: 0.5 }}
                            />
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                onClick={() => {
                                  setEditingCompetitorDescription(null);
                                  setCompetitorDescriptionValue('');
                                }}
                              >
                                Cancelar
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSaveCompetitorDescription(competitorName)}
                                disabled={saving}
                              >
                                Salvar
                              </Button>
                            </Box>
                          </Box>
                        ) : data.description ? (
                          <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                              {data.description}
                            </Typography>
                            <Tooltip title="Editar descrição">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingCompetitorDescription(competitorName);
                                  setCompetitorDescriptionValue(data.description);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Box sx={{ mb: 1 }}>
                            <Button
                              size="small"
                              startIcon={suggestingCompetitorDescription === competitorName ? <CircularProgress size={12} /> : <MIAIcon />}
                              onClick={() => handleSuggestCompetitorDescription(competitorName)}
                              disabled={suggestingCompetitorDescription === competitorName}
                              sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0 }}
                            >
                              Gerar descrição
                            </Button>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {data.keywordsData.map((kwData, idx) => {
                            const hasExclusionContextTags = kwData.exclusionContextTags?.length > 0;
                            const hasOptions = kwData.requiresWith || (kwData.excludeTerms && kwData.excludeTerms.length > 0) || hasExclusionContextTags;
                            return (
                              <Tooltip
                                key={idx}
                                arrow
                                placement="top"
                                title={
                                  hasOptions || kwData.tagsToApply?.length > 0 ? (
                                    <Box>
                                      {kwData.requiresWith && <Typography variant="caption">Requer: {kwData.requiresWith}</Typography>}
                                      {kwData.excludeTerms?.length > 0 && (
                                        <Typography variant="caption" display="block">
                                          Excluir: {kwData.excludeTerms.join(', ')}
                                        </Typography>
                                      )}
                                      {kwData.tagsToApply?.length > 0 && (
                                        <Typography variant="caption" display="block" sx={{ color: 'primary.light' }}>
                                          {kwData.tagsToApply.length} tag(s): {availableTags.filter(t => kwData.tagsToApply.includes(t._id)).map(t => t.name).join(', ')}
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : 'Clique para configurar'
                                }
                              >
                                <Chip
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {kwData.keyword}
                                      {kwData.requiresWith && <LinkIcon sx={{ fontSize: 12, opacity: 0.7 }} />}
                                      {kwData.excludeTerms?.length > 0 && <BlockIcon sx={{ fontSize: 12, opacity: 0.7 }} />}
                                      {kwData.tagsToApply?.length > 0 && (
                                        <TagIcon sx={{ fontSize: 12, opacity: 0.8, color: 'primary.main' }} />
                                      )}
                                      {hasExclusionContextTags && <TagIcon sx={{ fontSize: 12, opacity: 0.8, color: 'warning.main' }} />}
                                    </Box>
                                  }
                                  color="warning"
                                  variant={hasOptions ? 'filled' : 'outlined'}
                                  onClick={() => handleOpenKeywordOptions(kwData.keyword, 'competitor')}
                                  onDelete={() => handleRemoveCompetitorKeyword(kwData.keyword)}
                                  sx={{ cursor: 'pointer', borderRadius: '8px', fontSize: '0.825rem' }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Box>

                        {/* Adicionar Keyword Manual ao Concorrente Existente */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <TextField
                            size="small"
                            placeholder="Adicionar keyword..."
                            value={manualCompetitorKeywords[competitorName] || ''}
                            onChange={(e) => setManualCompetitorKeywords(prev => ({
                              ...prev,
                              [competitorName]: e.target.value
                            }))}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddManualCompetitorKeyword(competitorName)}
                            fullWidth
                          />
                          <Button
                            variant="contained"
                            color="warning"
                            onClick={() => handleAddManualCompetitorKeyword(competitorName)}
                            disabled={saving || !manualCompetitorKeywords[competitorName]?.trim()}
                          >
                            <AddIcon />
                          </Button>
                        </Box>

                        {/* Botões de ação MIA */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={suggestingKeywords ? <CircularProgress size={14} /> : <MIAIcon />}
                            onClick={() => handleSuggestMoreCompetitorKeywords(competitorName)}
                            disabled={suggestingKeywords}
                            sx={{ textTransform: 'none', fontSize: '0.725rem', borderRadius: 2 }}
                          >
                            Sugerir Keywords
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={suggestingCompetitorProducts === competitorName ? <CircularProgress size={14} /> : <ProductIcon />}
                            onClick={() => handleSuggestCompetitorProducts(competitorName, data.description)}
                            disabled={suggestingCompetitorProducts === competitorName}
                            sx={{ textTransform: 'none', fontSize: '0.725rem', borderRadius: 2 }}
                          >
                            Produtos
                          </Button>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Adicionar novo concorrente */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  Adicionar Novo Concorrente
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="Nome do concorrente"
                    value={newCompetitorName}
                    onChange={(e) => setNewCompetitorName(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <Tooltip title="Sugerir keywords com MIA">
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        onClick={handleSuggestCompetitorKeywords}
                        disabled={suggestingKeywords || !newCompetitorName.trim()}
                        startIcon={suggestingKeywords ? <CircularProgress size={16} /> : <MIAIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        Sugerir com MIA
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Keyword manual (opcional)"
                    value={newCompetitorKeyword}
                    onChange={(e) => setNewCompetitorKeyword(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleAddCompetitorKeyword}
                    disabled={saving || !newCompetitorKeyword.trim() || !newCompetitorName.trim()}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Configurações */}
        {activeTab === 2 && (
          <>
            <Grid item xs={12}>
              <Card sx={{ background: 'transparent', boxShadow: 'none', border: 'none', backgroundImage: 'none' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SettingsIcon color="primary" />
                    Configurações de Análise
                  </Typography>

                  <Grid container spacing={{ xs: 0, md: 3 }} sx={{ rowGap: { xs: 1.5, md: 0 } }}>
                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={briefing.settings?.autoAnalyze ?? true}
                            onChange={(e) => handleUpdateSettings({
                              ...briefing.settings,
                              autoAnalyze: e.target.checked
                            })}
                          />
                        }
                        label="Análise automática"
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Analisar exposição automaticamente ao adicionar artigos
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={briefing.settings?.useAI ?? true}
                            onChange={(e) => handleUpdateSettings({
                              ...briefing.settings,
                              useAI: e.target.checked
                            })}
                          />
                        }
                        label="Usar IA"
                      />
                      <Typography variant="caption" color="text.secondary" display="block">
                        Usar IA para analisar casos ambíguos
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Sensibilidade</InputLabel>
                        <Select
                          value={briefing.settings?.sensitivity || 'medium'}
                          label="Sensibilidade"
                          onChange={(e) => handleUpdateSettings({
                            ...briefing.settings,
                            sensitivity: e.target.value
                          })}
                        >
                          <MenuItem value="low">Baixa</MenuItem>
                          <MenuItem value="medium">Média</MenuItem>
                          <MenuItem value="high">Alta</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Quantas menções para considerar "Dedicada"
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Dicas */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1, display: 'flex', gap: 1.5, color: 'text.secondary' }}>
                <LightbulbIcon color="info" sx={{ mt: 0.2 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 0.5 }}>
                    Como funciona a análise de Exposição:
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <strong>Dedicada</strong>: Matéria focada na sua marca (muitas menções, sem concorrentes)<br />
                    <strong>Compartilhada</strong>: Sua marca aparece junto com concorrentes (comparativo, lista)<br />
                    <strong>Citação</strong>: Apenas uma menção breve da sua marca
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </>
        )}
      </Grid>

      {/* Dialog de Seleção de Keywords (Padronizado) */}
      <PlatformDialog
        open={keywordDialogOpen}
        onClose={() => setKeywordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <PlatformDialogTitle
          title={keywordDialogType === 'primary' ? 'Palavras-chave Sugeridas' : `Keywords para ${keywordDialogCompetitor}`}
          subtitle="Selecione as sugestões da MIA para monitoramento."
          icon={<MIAIcon />}
          onClose={() => setKeywordDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Clique para selecionar as palavras-chave que deseja adicionar:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestedKeywords.map((keyword, index) => (
              <Chip
                key={index}
                label={keyword}
                onClick={() => toggleKeywordSelection(keyword)}
                color={selectedSuggestedKeywords.includes(keyword) ? 'primary' : 'default'}
                variant={selectedSuggestedKeywords.includes(keyword) ? 'filled' : 'outlined'}
                icon={selectedSuggestedKeywords.includes(keyword) ? <CheckIcon sx={{ fontSize: '1rem !important' }} /> : undefined}
                sx={{ cursor: 'pointer', borderRadius: 2, fontWeight: 500 }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontWeight: 500 }}>
            {selectedSuggestedKeywords.length} de {suggestedKeywords.length} selecionadas
          </Typography>

          {/* Opção de vincular todas à marca/concorrente */}
          {briefing && selectedSuggestedKeywords.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={linkAllWithBrand}
                    onChange={(e) => setLinkAllWithBrand(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LinkIcon fontSize="small" color={linkAllWithBrand ? 'primary' : 'inherit'} />
                    <Typography variant="body2" fontWeight={500}>
                      Vincular todas com{' '}
                      <strong>
                        {keywordDialogType === 'primary' ? briefing?.name : keywordDialogCompetitor}
                      </strong>
                    </Typography>
                  </Box>
                }
              />
              {linkAllWithBrand && (
                <Alert severity="success" icon={<CheckIcon fontSize="small" />} sx={{ mt: 1, py: 0, borderRadius: 2 }}>
                  <Typography variant="caption">
                    As keywords só serão contadas se <strong>"{keywordDialogType === 'primary' ? briefing?.name : keywordDialogCompetitor}"</strong> também aparecer no texto.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {/* Seleção de tags para todas as keywords */}
          {briefing && selectedSuggestedKeywords.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                <TagIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight={600}>
                  Aplicar tags a todas as keywords
                </Typography>
              </Box>

              {tagForAllKeywords.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {tagForAllKeywords.map(tag => (
                    <Chip
                      key={tag._id}
                      label={tag.name}
                      size="small"
                      onDelete={() => setTagForAllKeywords(prev => prev.filter(t => t._id !== tag._id))}
                      color="primary"
                      variant="outlined"
                      sx={{ borderRadius: 1.5 }}
                    />
                  ))}
                </Box>
              )}

              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={availableTags.filter(t => !tagForAllKeywords.some(selected => selected._id === t._id))}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.inputValue) return option.inputValue;
                  return option.name;
                }}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  // Se há texto digitado, mostrar opção de criar nova tag
                  if (params.inputValue !== '') {
                    const inputLower = params.inputValue.toLowerCase().trim();
                    const exists = availableTags.some(
                      tag => tag.name.toLowerCase() === inputLower
                    );
                    if (!exists && inputLower.length > 0) {
                      filtered.push({
                        inputValue: params.inputValue.trim(),
                        name: `Criar "${params.inputValue.trim()}"`,
                        isNew: true
                      });
                    }
                  }
                  return filtered;
                }}
                value={[]}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione ou crie tags"
                    placeholder="Digite para buscar..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key || option._id || option.inputValue} {...otherProps}>
                      {option.isNew ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AddIcon fontSize="small" color="primary" />
                          <Typography color="primary">{option.name}</Typography>
                        </Box>
                      ) : (
                        option.name
                      )}
                    </li>
                  );
                }}
                onChange={(_, newValue) => {
                  if (newValue.length > 0) {
                    const last = newValue[newValue.length - 1];
                    
                    // Se for string, criou nova tag digitada
                    if (typeof last === 'string') {
                      handleCreateTagInline(last);
                    } else if (last && last.isNew) {
                      handleCreateTagInline(last.inputValue);
                    } else if (last && last._id && !tagForAllKeywords.some(t => t._id === last._id)) {
                      setTagForAllKeywords([...tagForAllKeywords, last]);
                    }
                  }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button 
            onClick={() => {
              setKeywordDialogOpen(false);
              setLinkAllWithBrand(false);
              setTagForAllKeywords([]);
            }}
            sx={{ fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSuggestedKeywords}
            disabled={saving || selectedSuggestedKeywords.length === 0}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            Adicionar ({selectedSuggestedKeywords.length})
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog de Opções de Keyword (Padronizado) */}
      <PlatformDialog
        open={keywordOptionsDialogOpen}
        onClose={() => setKeywordOptionsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <PlatformDialogTitle
          title={`Configurar Keyword: ${editingKeyword?.keyword}`}
          subtitle="Aprimore os critérios de correspondência desta palavra-chave."
          icon={<TuneIcon />}
          onClose={() => setKeywordOptionsDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* Palavra vinculada (requiresWith) */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
              <LinkIcon fontSize="small" color="primary" />
              Palavra Vinculada
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Esta keyword só será considerada se a palavra vinculada também aparecer no texto.
              Ex: "Urus" só conta se "Lamborghini" também estiver presente.
            </Typography>

            {/* Checkbox para vincular com a marca/concorrente */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={linkWithBrand}
                  onChange={(e) => {
                    setLinkWithBrand(e.target.checked);
                    setKeywordRequiresWith('');
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  Vincular com{' '}
                  <strong>
                    {editingKeyword?.type === 'primary' 
                      ? briefing?.name 
                      : briefing?.competitorKeywords?.find(k => k.keyword === editingKeyword?.keyword)?.competitor || 'Concorrente'
                    }
                  </strong>
                </Typography>
              }
              sx={{ mb: 1 }}
            />

            <TextField
              fullWidth
              size="small"
              placeholder="Ou digite outra palavra personalizada..."
              value={linkWithBrand ? '' : keywordRequiresWith}
              onChange={(e) => setKeywordRequiresWith(e.target.value)}
              disabled={linkWithBrand}
              sx={{ 
                borderRadius: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 2 }
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Tags a aplicar */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
              <TagIcon fontSize="small" color="primary" />
              Tags Automáticas
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              As tags abaixo serão aplicadas automaticamente aos artigos que contiverem esta keyword.
            </Typography>

            {selectedTagToApply.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {selectedTagToApply.map(tag => (
                  <Chip
                    key={tag._id}
                    label={tag.name}
                    size="small"
                    onDelete={() => setSelectedTagToApply(prev => prev.filter(t => t._id !== tag._id))}
                    color="primary"
                    variant="outlined"
                    sx={{ borderRadius: 1.5 }}
                  />
                ))}
              </Box>
            )}

            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={availableTags.filter(t => !selectedTagToApply.some(selected => selected._id === t._id))}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.inputValue) return option.inputValue;
                return option.name;
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== '') {
                  const inputLower = params.inputValue.toLowerCase().trim();
                  const exists = availableTags.some(
                    tag => tag.name.toLowerCase() === inputLower
                  );
                  if (!exists && inputLower.length > 0) {
                    filtered.push({
                      inputValue: params.inputValue.trim(),
                      name: `Criar "${params.inputValue.trim()}"`,
                      isNew: true
                    });
                  }
                }
                return filtered;
              }}
              value={[]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar ou criar tags"
                  placeholder="Digite o nome da tag..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key || option._id || option.inputValue} {...otherProps}>
                    {option.isNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon fontSize="small" color="primary" />
                        <Typography color="primary">{option.name}</Typography>
                      </Box>
                    ) : (
                      option.name
                    )}
                  </li>
                );
              }}
              onChange={(_, newValue) => {
                if (newValue.length > 0) {
                  const last = newValue[newValue.length - 1];

                  // Se for string, significa que o usuário criou uma nova tag digitada
                  if (typeof last === 'string') {
                    handleCreateTagIndividual(last);
                  } else if (last && last.isNew) {
                    handleCreateTagIndividual(last.inputValue);
                  } else if (last && last._id && !selectedTagToApply.some(t => t._id === last._id)) {
                    setSelectedTagToApply([...selectedTagToApply, last]);
                  }
                }
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Termos de exclusão */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
              <BlockIcon fontSize="small" color="error" />
              Termos de Exclusão
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Ocorrências da keyword dentro destes termos serão ignoradas na análise.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {(editingKeyword?.data?.excludeTerms || []).map((term, idx) => (
                <Box
                  key={`${term}-${idx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'start',
                    gap: 1,
                    p: 1.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'action.hover'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 96,
                      overflow: 'auto',
                      pr: 0.5
                    }}
                  >
                    {term}
                  </Typography>
                  <Tooltip title="Remover termo">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveExcludeTerm(term)}
                      sx={{ mt: -0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
              {(!editingKeyword?.data?.excludeTerms || editingKeyword.data.excludeTerms.length === 0) && (
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  Nenhum termo de exclusão configurado
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Adicionar termo de exclusão ou colar um trecho de boilerplate..."
                value={newExcludeTerm}
                onChange={(e) => setNewExcludeTerm(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    handleAddExcludeTerm();
                  }
                }}
                multiline
                minRows={2}
                maxRows={5}
                fullWidth
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Button
                variant="outlined"
                color="error"
                onClick={handleAddExcludeTerm}
                disabled={!newExcludeTerm.trim()}
                sx={{ borderRadius: 2, minWidth: 48 }}
              >
                <AddIcon />
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}>
              <TagIcon fontSize="small" color="warning" />
              Tags para Contexto de Exclusão
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Se todas as ocorrências da keyword estiverem dentro dos termos de exclusão, aplique estas tags no lugar das Tags Automáticas.
            </Typography>

            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={availableTags.filter(t => !selectedExclusionContextTags.some(selected => selected._id === t._id))}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.inputValue) return option.inputValue;
                return option.name;
              }}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== '') {
                  const inputLower = params.inputValue.toLowerCase().trim();
                  const exists = availableTags.some(
                    tag => tag.name.toLowerCase() === inputLower
                  );
                  if (!exists && inputLower.length > 0) {
                    filtered.push({
                      inputValue: params.inputValue.trim(),
                      name: `Criar "${params.inputValue.trim()}"`,
                      isNew: true
                    });
                  }
                }
                return filtered;
              }}
              value={selectedExclusionContextTags}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags para contexto de exclusão"
                  placeholder="Ex: Boilerplate"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key || option._id || option.inputValue} {...otherProps}>
                    {option.isNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddIcon fontSize="small" color="primary" />
                        <Typography color="primary">{option.name}</Typography>
                      </Box>
                    ) : (
                      option.name
                    )}
                  </li>
                );
              }}
              onChange={(_, newValue) => {
                const normalizedTags = [];
                newValue.forEach((item) => {
                  if (typeof item === 'string') {
                    handleCreateExclusionContextTag(item);
                  } else if (item?.isNew) {
                    handleCreateExclusionContextTag(item.inputValue);
                  } else if (item?._id && !normalizedTags.some(tag => tag._id === item._id)) {
                    normalizedTags.push(item);
                  }
                });
                setSelectedExclusionContextTags(normalizedTags);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button 
            onClick={() => {
              setKeywordOptionsDialogOpen(false);
              setSelectedTagToApply([]);
              setSelectedExclusionContextTags([]);
              setTagInputValue('');
            }}
            sx={{ fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveKeywordOptions}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog de Edição de Info do Briefing (Padronizado) */}
      <PlatformDialog 
        open={editInfoOpen} 
        onClose={() => setEditInfoOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Editar Briefing"
          subtitle="Atualize as informações contextuais da marca para a IA."
          icon={<EditIcon />}
          onClose={() => setEditInfoOpen(false)}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <TextField
             fullWidth
             label="Nome da Marca"
             value={editingBriefingName}
             onChange={(e) => setEditingBriefingName(e.target.value)}
             sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
             size="small"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Descrição do Negócio
            </Typography>
            <Tooltip title="Pedir sugestão para MIA">
              <Button
                size="small"
                startIcon={suggestingDescription ? <CircularProgress size={14} color="inherit" /> : <MIAIcon />}
                onClick={handleSuggestEditDescription}
                disabled={suggestingDescription || !editingBriefingName.trim()}
                sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 1.5 }}
              >
                {suggestingDescription ? 'Gerando...' : 'Sugerir com MIA'}
              </Button>
            </Tooltip>
          </Box>
          <TextField
             fullWidth
             multiline
             rows={4}
             placeholder="Descreva brevemente a marca para que a MIA entenda o contexto das buscas..."
             value={editingBriefingDescription}
             onChange={(e) => setEditingBriefingDescription(e.target.value)}
             sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
           <Button onClick={() => setEditInfoOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
           <Button 
            variant="contained" 
            onClick={handleUpdateBriefingInfo} 
            disabled={saving || !editingBriefingName.trim()} 
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            Salvar Alterações
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog de Confirmação de Exclusão (Padronizado) */}
      <PlatformDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <PlatformDialogTitle
          title="Excluir Briefing?"
          subtitle="Esta ação removerá todas as keywords configuradas."
          icon={<DeleteIcon />}
          onClose={() => setDeleteDialogOpen(false)}
          color="error"
        />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Tem certeza que deseja excluir o briefing <strong>{briefing?.name}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            A configuração de palavras-chave para monitoramento será perdida permanentemente.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1.5 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            onClick={confirmDeleteBriefing} 
            color="error" 
            variant="contained" 
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Excluir
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Snackbar */}
      <ModernToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        duration={4000}
        position="bottom-center"
      />
    </Container>
  );
};

export default BriefingPage;
